// Helpers for the opt-in sub-process redesign projection returned by
// /cms/rl-optimize (`subprocess_redesign_projection`) and /cms/comparison-report
// (`engines[].projection`). See SUBPROCESS_REDESIGN_FRONTEND_API.md.
//
// A process can embed sub-processes (BPMN callActivity). Normally those are frozen
// during the parent's redesign, so a parent whose cycle time mostly sits inside its
// children honestly reports "no change justified". With the flag on, the backend
// redesigns the children too and prices their effect into the parent.
//
// Two rules here are not style choices — getting them wrong makes the UI state
// something false:
//   Rule A — when the projection is present, `as_is` and `to_be` are BOTH the
//            projected figures and are typically equal. The real current state is
//            `projection.true_as_is`. Rendering as_is → to_be shows 0% improvement.
//            Always read the baseline through getHeadlineAsIs().
//   Rule B — these figures assume the child redesigns are adopted. They are
//            proposals, not saved state, so anything derived from them must be
//            labelled as a projection (see PROJECTION_DISCLAIMER).

export const SUBPROCESS_SOURCE = "subprocess";

/**
 * Default state of the "include sub-processes" opt-in, shared by all four surfaces
 * that offer it (greedy/RL/BPR redesign and the comparison report) so they cannot
 * drift apart.
 *
 * On by default: a parent whose cycle time mostly sits inside its children otherwise
 * reports "no change justified" and hides the very improvements the analyst is
 * looking for. The cost is real though — one extra engine run per sub-process, and
 * for greedy it also forces the synchronous /cms/optimize/{id}/json endpoint, since
 * /start does not accept the flag. Any surface that starts a run automatically must
 * pass THIS constant rather than a literal, so the checkbox never claims a flag the
 * run did not actually use.
 */
export const DEFAULT_INCLUDE_SUBPROCESS = true;

// Engines that predate this feature send no `source` at all — absent means the
// parent's own heuristic, never "unknown".
export function isSubprocessRow(row) {
  return row?.source === SUBPROCESS_SOURCE;
}

export function getProjection(result) {
  return result?.subprocess_redesign_projection || null;
}

/**
 * The baseline the headline KPI must be measured against.
 *
 * Written as an unconditional read rather than a flag-dependent branch: with the
 * flag off (or on a process with no sub-processes) `true_as_is` is absent and this
 * collapses to today's behaviour, so there is no code path that can pick the
 * projected baseline by mistake.
 */
export function getHeadlineAsIs(result) {
  return getProjection(result)?.true_as_is ?? result?.as_is ?? null;
}

/**
 * Percent improvement, matching the backend's `measured_verdict` convention:
 * positive = better (time/cost went down), negative = worse.
 */
export function improvementPct(asIsValue, toBeValue) {
  const a = Number(asIsValue);
  const b = Number(toBeValue);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) return null;
  return ((a - b) / a) * 100;
}

/**
 * Every heuristic applied inside a child, from whichever key the backend sent.
 * `subprocess_heuristics` is the flat convenience array; falling back to a walk of
 * `heuristic_table` keeps this working if only the table is populated.
 */
export function collectSubprocessRows(result) {
  if (Array.isArray(result?.subprocess_heuristics)) {
    return result.subprocess_heuristics.filter(isSubprocessRow);
  }
  const sections = Array.isArray(result?.heuristic_table) ? result.heuristic_table : [];
  return sections.flatMap((s) => (s?.rows || []).filter(isSubprocessRow));
}

/**
 * Splits one heuristic_table section's rows into the parent's own rows and one
 * group per child, so the two never interleave — a user must never read
 * "Trusted Party" as applied to the main process when it was applied inside a child.
 *
 * Child order follows first appearance in `rows` (the backend appends child rows
 * after the parent's own), so the grouping never reorders what the agent did.
 */
export function splitAppliedRows(rows) {
  const parentRows = [];
  const groups = [];
  const byKey = new Map();

  for (const row of rows || []) {
    if (!isSubprocessRow(row)) {
      parentRows.push(row);
      continue;
    }
    // The `subprocess` block is documented as always present on a source="subprocess"
    // row, but a missing one must still render as a sub-process rather than silently
    // joining the parent's list — that would be the exact misattribution above.
    const sp = row.subprocess || {};
    const key = sp.child_process_id ?? `unnamed-${groups.length}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        childProcessId: sp.child_process_id ?? null,
        childProcessName: sp.child_process_name || "Sub-process",
        childAsIs: sp.child_as_is || null,
        childToBe: sp.child_to_be || null,
        parentImpact: sp.parent_impact || null,
        rows: [],
      };
      byKey.set(key, group);
      groups.push(group);
    }
    group.rows.push(row);
  }

  return { parentRows, childGroups: groups };
}

/**
 * Children the backend could not redesign. They carry no as_is/to_be/parent_impact,
 * so they never appear in the heuristic grouping and would otherwise vanish from the
 * UI entirely — the analyst would see 2 of 3 children and no hint the third failed.
 */
export function getFallbackChildren(projection) {
  const children = Array.isArray(projection?.children) ? projection.children : [];
  return children.filter((c) => c && c.status !== "redesigned");
}

// The two engines that carry a projection send its baseline/target objects in
// different vocabularies: RL uses {cycle_time, resource_cost} (matching its own
// as_is/to_be), BPR uses {cycle_time_minutes, labor_cost_per_case} (matching
// cost_time_comparison). Centralizing the read here means every consumer sees one
// shape regardless of which engine sent it.
const readCycleTime = (o) => {
  const v = o?.cycle_time ?? o?.cycle_time_minutes;
  return v == null ? null : Number(v);
};
const readCost = (o) => {
  const v = o?.resource_cost ?? o?.labor_cost_per_case;
  return v == null ? null : Number(v);
};

// Which cost the projection's cost figure actually is — the two fields it can come
// from are not the same metric. RL's `resource_cost` is scoped to the Responsible
// role only (RL_OPTIMIZATION_FRONTEND_API.md); BPR's `labor_cost_per_case` covers the
// whole process, the same scope the shared comparison baseline uses. A consumer that
// prints one of these under the label "Labor cost" without checking which it got
// would put RL's narrower number on the wrong shelf.
const costScope = (o) => {
  if (o?.resource_cost != null) return "responsible_only";
  if (o?.labor_cost_per_case != null) return "labor_cost_per_case";
  return null;
};

const sumParentImpact = (projection, key) => {
  const children = Array.isArray(projection?.children) ? projection.children : [];
  return children.reduce((sum, c) => {
    const saved = Number(c?.parent_impact?.[key]);
    return Number.isFinite(saved) ? sum + saved : sum;
  }, 0);
};

const redesignedChildCount = (projection) =>
  (Array.isArray(projection?.children) ? projection.children : [])
    .filter((c) => c?.status === "redesigned").length;

const metric = (asIs, toBe) => {
  if (!Number.isFinite(asIs) || !Number.isFinite(toBe)) return null;
  const saved = asIs - toBe;
  return { as_is: asIs, to_be: toBe, saved, reduction_pct: asIs === 0 ? null : (saved / asIs) * 100 };
};

/**
 * The parent's figures with every child redesign adopted, for a surface that has the
 * projection but not the engine's own sub-process-inclusive result — the comparison
 * report, whose scored KPIs deliberately exclude it.
 *
 * Two shapes, because the two engines compute this differently:
 *   - BPR rolls the parent up ITSELF and sends the answer directly on
 *     `projection.projected` — every child's `parent_impact` is null (the external
 *     model doesn't apportion its effect by branch), so there is nothing to sum and
 *     the rolled-up figure must be read as-is, not derived.
 *   - RL sends no such roll-up. Its per-child `parent_impact.cycle_time_saved` is
 *     branch-weighted and summable, so the parent figure is built from those parts.
 *
 * Either way this stays entirely within `projection`, self-consistent on the engine's
 * own scale — nothing here touches the shared CMS baseline, so no two measurement
 * systems are mixed. For the same reason the result is NOT comparable across engines
 * and must be presented apart from the shared columns.
 *
 * It also stops short of the Redesign tab's headline, which additionally folds in the
 * parent's own NVA elimination and heuristics. This covers the sub-processes only.
 */
export function projectedParentKpis(projection) {
  if (projection?.projected) {
    const asIsTime = readCycleTime(projection.true_as_is);
    const asIsCost = readCost(projection.true_as_is);
    const toBeTime = readCycleTime(projection.projected.to_be);
    const toBeCost = readCost(projection.projected.to_be);
    if (asIsTime == null && asIsCost == null) return null;
    return {
      cycle_time: metric(asIsTime, toBeTime),
      resource_cost: metric(asIsCost, toBeCost),
      cost_scope: costScope(projection.true_as_is),
      cost_unit: projection.true_as_is?.cost_unit || "USD",
      redesigned_child_count: redesignedChildCount(projection),
    };
  }

  const base = projection?.true_as_is;
  if (!base) return null;
  const cycleAsIs = readCycleTime(base);
  const costAsIs = readCost(base);
  if (cycleAsIs == null && costAsIs == null) return null;

  const cycleSaved = sumParentImpact(projection, "cycle_time_saved");
  const costSaved = sumParentImpact(projection, "resource_cost_saved");

  return {
    cycle_time: cycleAsIs == null ? null : metric(cycleAsIs, cycleAsIs - cycleSaved),
    resource_cost: costAsIs == null ? null : metric(costAsIs, costAsIs - costSaved),
    cost_scope: costScope(base),
    cost_unit: base.cost_unit || "USD",
    redesigned_child_count: redesignedChildCount(projection),
  };
}

/**
 * Total cycle time the projection claims the child redesigns save the parent —
 * the same number `projectedParentKpis` derives its cycle-time row from, exposed on
 * its own for callers (like `projectionPricedIntoKpis`) that only need the one figure.
 */
export function claimedChildSavings(projection) {
  return projectedParentKpis(projection)?.cycle_time?.saved || 0;
}

/**
 * In the comparison report an engine can carry a projection while its shared-baseline
 * KPIs are scored on this process alone — the child redesigns are listed but never
 * priced into the compared numbers. A PROJECTED badge over figures that do not
 * contain the projection promises an improvement the column does not show.
 *
 * `kpis_are_projected` is the backend's own explicit answer and is trusted whenever
 * present — its docs say to read it rather than infer from the block's presence,
 * precisely because the three engines differ. The numeric inference below is kept
 * only for responses that predate the field: it asks whether the scored TO-BE
 * actually moved by anything like the claimed child saving.
 */
export function projectionPricedIntoKpis(projection, dq) {
  if (typeof projection?.kpis_are_projected === "boolean") {
    return projection.kpis_are_projected;
  }
  const claimed = claimedChildSavings(projection);
  if (!(claimed > 0)) return true;
  const asIs = Number(dq?.time?.as_is);
  const toBe = Number(dq?.time?.to_be);
  if (!Number.isFinite(asIs) || !Number.isFinite(toBe)) return true;
  return asIs - toBe >= claimed * 0.5;
}

/**
 * Can a combined "parent with every child redesigned" figure be reported at all?
 *
 * The greedy engine says no, explicitly and permanently: its figures come from the
 * CMS what-if, whose inputs are keyed to the parent's own task ids with no way to
 * express a redesigned sub-process — and compounding the parent's and children's
 * percentages instead would double-count wherever they meet on the critical path.
 * So `parent_rollup.available === false` means any combined row we might render
 * would be one we invented; RL and BPR omit the block entirely and can each measure
 * the combined state directly.
 */
export function parentRollupUnavailableReason(projection) {
  const rollup = projection?.parent_rollup;
  if (!rollup || rollup.available !== false) return null;
  return rollup.unavailable_reason || "This engine cannot report a combined parent figure.";
}

/**
 * Children the engine actually redesigned, with the per-child figures normalized
 * across the three engines' differing key names. Greedy adds two fields the others
 * have no equivalent for, and both change what the numbers mean:
 *   - `measurement_gap`: the CMS's stored flow analysis and its what-if disagree on
 *     this child BEFORE any redesign. The reductions are what-if-to-what-if, so the
 *     gap is excluded from them — but a reader comparing against the stored figure
 *     elsewhere in the app will see a difference that is measurement, not saving.
 *   - `stored_as_is`: the stored figure that gap is measured against.
 */
export function normalizedChildren(projection) {
  const children = Array.isArray(projection?.children) ? projection.children : [];
  return children
    .filter((c) => c && c.status === "redesigned")
    .map((c) => ({
      id: c.child_process_id,
      name: c.child_process_name || "Sub-process",
      asIsCycle: readCycleTime(c.as_is),
      toBeCycle: readCycleTime(c.to_be),
      asIsCost: readCost(c.as_is),
      toBeCost: readCost(c.to_be),
      cycleReductionPct: c.cycle_time_reduction_pct ?? null,
      costReductionPct: c.labor_cost_reduction_pct ?? null,
      appliedHeuristics: Array.isArray(c.applied_heuristics) ? c.applied_heuristics : [],
      nvaElimination: c.nva_elimination || null,
      measurementGap: c.measurement_gap || null,
      storedAsIs: c.stored_as_is || null,
      parentImpact: c.parent_impact || null,
    }));
}

const normalizeName = (name) => String(name || "").trim().toLowerCase();

/**
 * Did this row actually get applied? The three surfaces spell it differently —
 * comparison-report rows carry `verdict`, RL heuristic_table rows carry
 * `rule_met`/`implemented`, BPR rows carry `isApplied` — and a row that says none of
 * them is not applied.
 */
function isAppliedRow(row) {
  return (
    row?.verdict === "applied" ||
    row?.rule_met === "applied" ||
    row?.implemented === true ||
    row?.isApplied === true
  );
}

/**
 * Child names keyed by heuristic name, for the comparison report's cross-engine
 * matrix. Heuristic name is the only key the matrix rows and `engines[].heuristics[]`
 * share, so the join has to go through it. Values are Sets — a heuristic applied
 * inside 2+ children names all of them, never collapses to a vague "Sub-processes"
 * placeholder when the real names are known and short enough to just say.
 *
 * Only a heuristic the engine APPLIED to the parent suppresses the chip. The engine
 * commonly reports the same heuristic twice — `rule_not_met` on the parent and
 * `applied` inside a child — and the matrix collapses that pair into one "applied"
 * cell. Suppressing on mere presence left that cell bare, so it read as applied to
 * this process when the engine had explicitly said the parent does not qualify.
 * A heuristic genuinely applied in both places is still left unchipped: one chip on
 * a shared cell would claim the whole cell is a sub-process result.
 */
export function subprocessBadgesByHeuristic(engine) {
  const rows = Array.isArray(engine?.heuristics) ? engine.heuristics : [];
  const parentAppliedNames = new Set(
    rows
      .filter((r) => !isSubprocessRow(r) && isAppliedRow(r))
      .map((r) => normalizeName(r.heuristic ?? r.name)),
  );
  const map = new Map();
  for (const row of rows) {
    if (!isSubprocessRow(row)) continue;
    const key = normalizeName(row.heuristic ?? row.name);
    if (!key || parentAppliedNames.has(key)) continue;
    const childName = row.subprocess?.child_process_name || "Sub-process";
    if (!map.has(key)) map.set(key, new Set());
    map.get(key).add(childName);
  }
  return map;
}

// BPR's raw model vocabulary calls three heuristics "task_*" where the FBPM taxonomy
// used everywhere else in this response — including this same engine's own
// parent-level `code` field — calls them "activity-*": task_elimination,
// task_composition, task_automation. Everything else converts cleanly with a plain
// underscore->hyphen swap ("knock_out" -> "knock-out"). Missing this mapping meant
// "task_composition" (a child's applied_heuristics entry) never matched the matrix
// row coded "activity-composition", so that child's work silently vanished — not
// mislabelled, just gone, with nothing to say it hadn't been considered.
const BPR_RAW_TO_CANONICAL_CODE = {
  task_elimination: "activity-elimination",
  task_composition: "activity-composition",
  task_automation: "activity-automation",
};

const normalizeCode = (code) => {
  const raw = String(code || "").trim().toLowerCase();
  return BPR_RAW_TO_CANONICAL_CODE[raw] || raw.replace(/_/g, "-");
};

/**
 * Same purpose as subprocessBadgesByHeuristic — which (engine, heuristic) cells in
 * the comparison matrix are sub-process results — for an engine whose comparison
 * response carries no source="subprocess" rows at all. BPR is this today: unlike RL,
 * its `heuristics[]` here is only ever the parent's own, one row per code, with no
 * per-child rows folded in. The per-child detail exists, just one level up, on
 * `projection.children[].applied_heuristics` (the model's own flat list of what it
 * applied inside that child) — this rebuilds the same map from there instead.
 *
 * Matches by `code`, not by name: `applied_heuristics` sends the model's raw
 * snake_case ("knock_out"), which converts cleanly to the matrix's kebab-case `code`
 * but not reliably to `name`, which is inconsistently cased across rows.
 *
 * Deliberately NOT suppressed when the parent also applied the same code — unlike
 * the name-keyed version above. That suppression exists for RL because RL's verdict
 * is genuinely ambiguous: the backend flattens parent- and child-sourced rows into
 * one boolean per cell, so an unqualified badge risks claiming a parent's own result
 * as a child's. BPR carries no such ambiguity — its parent-level `heuristics[]` and
 * each child's `applied_heuristics` are two independent, already-disambiguated lists
 * — so suppressing here would not be resolving an ambiguity, it would be hiding a
 * plain fact: on process 1979, trusted-party/extra-resources/numerical-involvement
 * are applied at the parent AND inside every one of its sub-processes, and all of
 * that is real, simultaneously true information the reader should see.
 */
export function subprocessBadgesByCodeFromProjection(engine) {
  const children = Array.isArray(engine?.projection?.children) ? engine.projection.children : [];
  if (children.length === 0) return new Map();

  const map = new Map();
  for (const child of children) {
    if (child?.status !== "redesigned") continue;
    const childName = child.child_process_name || "Sub-process";
    for (const raw of child.applied_heuristics || []) {
      const code = normalizeCode(raw);
      if (!code) continue;
      if (!map.has(code)) map.set(code, new Set());
      map.get(code).add(childName);
    }
  }
  return map;
}

// Counterpart to lookupSubprocessBadge for the code-keyed map above.
export function lookupSubprocessBadgeByCode(badgeMap, code) {
  return lookupSubprocessBadge(badgeMap, code, normalizeCode);
}

/**
 * Every child name a heuristic traces back to, as an array — never a vague
 * "Sub-processes" placeholder standing in for names the map already knows. Returns
 * undefined only when this (engine, heuristic) pair isn't a sub-process result at
 * all, so callers can tell "no sub-process involvement" apart from "involved, and
 * here's who". `normalize` defaults to the name-keyed map's own normalizer;
 * lookupSubprocessBadgeByCode passes the code-keyed one instead.
 */
export function lookupSubprocessBadge(badgeMap, heuristicName, normalize = normalizeName) {
  if (!badgeMap) return undefined;
  const key = normalize(heuristicName);
  const set = badgeMap.get(key);
  if (!set || set.size === 0) return undefined;
  return Array.from(set);
}

export const PROJECTION_DISCLAIMER =
  "These figures assume the sub-process redesigns below are adopted. Those redesigns are proposals and are not saved in the CMS.";
